import { useState, useEffect, useContext } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttTable,
    SttLabelFee,
    SttChipPForm,
    SttLabelDate,
    SttTopControls,
} from '../../../sporttia/all';
import StudentDialog from '../../../components/dialogs/StudentDialog';
import { formatPriceByLocale } from '../../../lib/utils';

type MshipData = {
    id: number;
};

type Props = {
    mship: MshipData;
};

type FilterState = {
    actName: string;
    onTime: string;
};

type StudentData = {
    id: number;
    created: string;
    ini: string;
    end: string;
    rate: {
        name: string;
        period: string;
        fee: number;
        activity: {
            name: string;
        };
    };
    enrollment?: {
        price: number;
    } | null;
    feePaymentForm?: string;
};

type StudentsResponse = {
    rows?: StudentData[];
    total?: number;
};

type PaginationParams = {
    page: number;
    numRows: number;
};

export default function MshipStudents({ mship }: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [students, setStudents] = useState<StudentsResponse>({});
    const [studentDialogId, setStudentDialogId] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [filter, setFilter] = useState<FilterState>({
        actName: '',
        onTime: 'Y',
    });

    const loadStudents = (p: PaginationParams = { page: 1, numRows: 20 }) => {
        setLoading(true);
        cxt?.api('GET', `/mships/${mship.id}/students`, {
            params: {
                page: p.page,
                rows: p.numRows,
                actName: filter.actName,
                onTime: filter.onTime === 'Y',
            },
            success: (r: any) => {
                setStudents(r);
                setLoading(false);
            },
        });
    };

    useEffect(() => {
        loadStudents();
    }, [filter.onTime]);

    const handleFormSearch = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) => {
        if (!name) return;
        setFilter({ ...filter, [name]: value });
    };

    return (
        <>
            <SttTopControls
                mb={2}
                fields={
                    [
                        {
                            type: 'text',
                            name: 'actName',
                            caption: t('Activity'),
                            value: filter.actName,
                            onChange: handleFormSearch,
                            onEnterKey: loadStudents,
                            xs: 12,
                            sm: 6,
                            md: 5,
                        },
                        {
                            type: 'select',
                            name: 'onTime',
                            caption: t('Status'),
                            value: filter.onTime,
                            onChange: handleFormSearch,
                            options: [
                                { caption: t('All'), value: 'N' },
                                { caption: t('Active'), value: 'Y' },
                            ],
                            xs: 12,
                            sm: 6,
                            md: 4,
                        },
                    ] as any
                }
                onFilter={loadStudents as any}
            />

            <SttTable
                data={students}
                autoload={false}
                loading={loading}
                onClickRow={(student: StudentData) =>
                    setStudentDialogId(student.id)
                }
                columns={
                    [
                        {
                            field: 'classRate',
                            align: 'left',
                            title: `${t('Activity')} / ${t('Rate')}`,
                            value: (r: StudentData) => (
                                <>
                                    <Box>
                                        <b>{r.rate.activity.name}</b>
                                    </Box>
                                    <Box>{r.rate.name}</Box>
                                </>
                            ),
                        },
                        {
                            field: 'period',
                            type: 'period',
                            title: t('Period'),
                            value: (r: StudentData) => ({
                                ini: r.ini,
                                end: r.end,
                            }),
                        },
                        {
                            field: 'inscription',
                            title: t('Inscription'),
                            value: (r: StudentData) => (
                                <>
                                    <SttLabelDate
                                        date={r.created}
                                        timezoneName={cxt?.sc?.timezone?.name}
                                        locale={cxt?.lang?.key}
                                    />
                                    <br />
                                    {r.enrollment && r.enrollment.price
                                        ? `${formatPriceByLocale(
                                              r.enrollment.price,
                                              cxt?.sc?.city?.country?.currency,
                                          )}`
                                        : null}
                                </>
                            ),
                        },
                        {
                            field: 'fee',
                            title: t('Fee'),
                            value: (r: StudentData) => (
                                <>
                                    <SttLabelFee
                                        interval={r.rate.period as any}
                                        price={r.rate.fee}
                                    />
                                    {r.rate.fee ? (
                                        <SttChipPForm
                                            pf={r.feePaymentForm as any}
                                        />
                                    ) : null}
                                </>
                            ),
                        },
                    ] as any
                }
                onFetch={loadStudents as any}
            />

            {studentDialogId && (
                <StudentDialog
                    open
                    idStudent={studentDialogId}
                    onClose={() => {
                        setStudentDialogId(null);
                    }}
                    onSave={loadStudents}
                />
            )}
        </>
    );
}
